import React, { useEffect, useRef, useState } from 'react';
import SignatureCanvas from 'react-signature-canvas';
import * as Icons from '../../assets/svg/icons';

export const InstoreDeliveryAssignmentAlert = ({ assignment }) => {
	if (!assignment?.assigned) {
		return null;
	}

	const ownerName = assignment?.owner?.name || 'the delivery team';

	return (
		<div className="my-3 rounded-lg border border-amber-200 bg-amber-50 px-4 py-3 text-sm text-amber-900">
			<p className="font-semibold">Medicines are currently with the delivery side.</p>
			<p className="mt-1">Assigned delivery owner: {ownerName}</p>
			<p className="mt-1 text-xs text-amber-800">
				You can still continue dispensing in-store. After delivery, choose whether to take handover now or return duplicate stock at end of day.
			</p>
		</div>
	);
};

export const InstoreDeliveryStockDecisionModal = ({
	showModal,
	patientName,
	signingAsName = '',
	onTakeNow,
	onReturnEndOfDay,
	onCancel,
}) => {
	const sigRef = useRef(null);
	const [stockDecisionSignature, setStockDecisionSignature] = useState(null);
	const [signatureError, setSignatureError] = useState('');

	useEffect(() => {
		if (!showModal) {
			setStockDecisionSignature(null);
			setSignatureError('');
			sigRef.current?.clear();
		}
	}, [showModal]);

	if (!showModal) {
		return null;
	}

	const handleSignatureEnd = () => {
		const signatureDataURL = sigRef?.current?.toDataURL();
		setStockDecisionSignature(signatureDataURL || null);
		if (signatureDataURL) {
			setSignatureError('');
		}
	};

	const handleClearSignature = () => {
		sigRef?.current?.clear();
		setStockDecisionSignature(null);
		setSignatureError('');
	};

	const handleTakeNow = () => {
		if (!stockDecisionSignature) {
			setSignatureError('Signature is required.');
			return;
		}

		setSignatureError('');
		onTakeNow?.(stockDecisionSignature);
	};

	return (
		<div className="fixed inset-0 z-[9999] flex items-center justify-center p-4" role="dialog" aria-modal="true">
			<div
				className="absolute inset-0 z-[1]"
				style={{ backgroundColor: 'rgba(0, 0, 0, 0.5)' }}
				onClick={onCancel}
				onKeyDown={(e) => e.key === 'Escape' && onCancel?.()}
				aria-hidden="true"
			/>
			<div
				className="relative z-[2] w-full max-w-md overflow-hidden rounded-lg border border-gray-200 bg-white"
				style={{ boxShadow: '0 25px 50px -12px rgba(0, 0, 0, 0.25), 0 0 0 1px rgba(0, 0, 0, 0.05)' }}
			>
				<div className="border-b border-dashed p-3 px-4">
					<h3 className="text-sm font-bold text-gray-900">Delivery-Side Stock Decision</h3>
				</div>
				<div className="space-y-4 p-6">
					<p className="text-sm text-gray-700">
						{patientName ? `${patientName} was dispensed in-store.` : 'This patient was dispensed in-store.'}
					</p>
					<p className="text-sm text-gray-700">
						{/* How should the stock with the driver be handled? */}
						Choose what should happen to the delivery-side stock that is still physically with the driver or assistant.
						{/* Are you sure you want to hand over this stock? */}
					</p>
					<div className="rounded-lg border border-gray-200 bg-gray-100 p-4">
						<h4 className="text-base text-gray-800">Signature</h4>
						<div
							className="mt-3 relative w-full overflow-hidden rounded-lg border bg-white min-h-[200px]"
							style={{ borderColor: signatureError ? 'red' : '#e5e7eb' }}
						>
							<SignatureCanvas
								ref={sigRef}
								penColor="black"
								clearOnResize={false}
								canvasProps={{ className: 'signature w-full', style: { minHeight: '200px' } }}
								onEnd={handleSignatureEnd}
							/>
							<button
								type="button"
								onClick={handleClearSignature}
								className="absolute bottom-2 right-2 flex items-center gap-1.5 rounded border border-gray-200 bg-white px-3 py-1.5 text-xs font-medium uppercase text-gray-800 hover:bg-gray-50"
							>
								<Icons.ClearPatientSignatureIcon />
								Clear
							</button>
						</div>
						{signingAsName ? (
							<p className="mt-3 text-sm font-medium text-red-600">
								Signing as {signingAsName}
							</p>
						) : null}
						{signatureError ? (
							<p className="mt-2 text-sm text-red-600">{signatureError}</p>
						) : null}
					</div>
					<div>
						<div className="grid grid-cols-2 gap-2">
							<button
								type="button"
								onClick={handleTakeNow}
								className="w-full rounded-md bg-blue-700 px-3 py-2.5 text-sm font-semibold text-white hover:bg-blue-800"
							>
								Handover Now
							</button>
							<button
								type="button"
								onClick={onReturnEndOfDay}
								className="w-full rounded-md px-3 py-2.5 text-sm font-semibold text-white"
								style={{ backgroundColor: '#16a34a' }}
							>
								Repackaged
							</button>
						</div>
						<button
							type="button"
							onClick={onCancel}
							className="mt-2 w-full rounded-md bg-gray-200 px-3 py-2.5 text-sm font-semibold text-gray-800 hover:bg-gray-300"
						>
							Cancel
						</button>
					</div>
				</div>
			</div>
		</div>
	);
};
